export default {
  Helmet: {
    title: 'Quilt: Care Plan',
  },
  title: 'Care Plan',
  subtitle: 'Review and refine your personalized care plan',
  today: 'Today',
  tomorrow: 'Tomorrow',
  informationTiles: {
    appointments: {
      title: 'Today\'s Schedule',
      noAppointments: 'No appointments',
      appointment: 'Appointment',
      appointments: 'Appointments',
      remaining: '#{remaining} remaining',
      nextAppointment: 'Next: #{caregiverName} #{time}',
      noMoreToday: 'No more appointments today',
    },
    hoursWorked: {
      title: 'Hours This Week',
      noHours: 'No hours this week',
      hoursRemaining: '#{remaining} hours remaining',
      noHoursScheduled: 'No hours scheduled',
    },
    costs: {
      title: 'Current Weekly Cost',
      noCosts: 'No costs this week',
      currentCost: 'Current week: $#{currentCost}',
      totalCost: 'Forecasted week: $#{totalCost}',

    },
  },
  upcoming: {
    title: 'Appointments',
    description: 'Your schedule for today and tomorrow.',
    noAppointments: 'No events scheduled for today or tomorrow.',

  },
  sections: {
    nextSteps: {
      title: 'What\'s next?',
      steps: {
        review: {
          title: 'Caregiver profile review',
          subtitle: '3-5 days',
          altSubtitle: 'Profiles ready',
          buttonText: 'Review profiles',
        },
        book: {
          title: 'Book a call with your care manager',
          subtitle: 'Your first step is here',
          buttonText: 'Book a Call',
        },
        meetManager: {
          title: 'Meet your care manager',
          subtitle: 'Scheduled at',
          altSubtitle: 'Scheduled',
          upcoming: 'Upcoming meeting',
          buttonText: '#{time}',
        },
        meet: {
          title: 'Meet & greet',
          subtitle: 'Date not set',
          buttonText: 'Set date',
        },
        start: {
          title: 'Start date',
          subtitle: 'Date not set',
          buttonText: 'Set date',
        },
      },
    },
    overview: {
      title: 'Overview',
      careTypes: {
        morning: {
          name: 'Morning Care',
          time: '8:00 AM - 12:00 PM',
        },
        midday: {
          name: 'Midday Care',
          time: '12:00 PM - 2:00 PM',
        },
        afternoon: {
          name: 'Afternoon Care',
          time: '2:00 PM - 5:00 PM',
        },
        evening: {
          name: 'Evening Care',
          time: '5:00 PM - 8:00 PM',
        },
        overnight: {
          name: 'Overnight Care',
          time: '8:00 PM - 8:00 AM',
        },
      },
      hours: {
        '1-hour': '1 Hour',
        '2-3-hours': '2-3 Hours',
        '4-6-hours': '4-6 Hours',
        '8-12-hours': '8-12 Hours',
        'other': '',
      },
    },
    info: {
      title: 'Your partner in care: what to expect with CareQuilt',
      subtitle:
        'Reliable, consistent, and compassionate senior care designed for the long term. We specialize in matching your family with a single, dedicated caregiver supported by our Care Operations team, giving you the peace of mind to stop managing care and start being a family again.',

      howCareQuiltWorksTitle: 'How CareQuilt works',
      howCareQuiltWorks:
        'We focus on **recurring care** because consistency leads to better health outcomes and stronger bonds for your loved one.',

      identifyCareNeedsTitle: 'Identify care needs',
      identifyCareNeeds:
        'We understand your loved one\'s routine, care needs, and personality.',

      setRecurringScheduleTitle: 'Set a recurring schedule',
      setRecurringSchedule:
        'We understand your loved one\'s routine, care needs, and personality.',

      perfectMatchTitle: 'The perfect match',
      perfectMatch:
        'We find your caregiver match based on skills, personality, and availability.',

      ongoingSupportTitle: 'Ongoing support',
      ongoingSupport:
        'Our in-house Care Operations Team manages the logistics so the care always runs smoothly.',

      mutualOptInAdvantageTitle: 'The "mutual opt-in" advantage',
      mutualOptInAdvantage: [
        'Our model is "mutual opt-in," meaning families choose their caregivers,',
        'and caregivers also choose the families they work with.',
        'This ensures that every person in the home wants to be there,',
        'creating a deeper level of trust and commitment.',
        'To ensure long-term success, we recommend a **3-hour minimum per visit**.',
        'This makes the schedule more sustainable for caregivers',
        'and allows enough time for truly meaningful engagement',
        'and quality care for your loved one.',
      ].join(' '),

      choosingYourCaregiverTitle: 'Choosing your caregiver',
      choosingYourCaregiver:
        'You are in the driver\'s seat. We curate the best matches, but you make the final call. Our "Top 1%" vetting process ensures that every profile you see represents the highest standard of care.',

      theProcessTitle: 'The process',
      theProcess:
        'Share Care Needs > Set Schedule > Review Caregiver Matches > Meet & Greet with your selection > Start Care.',

      careQuiltStandardTitle: 'The CareQuilt standard',
      careQuiltStandard:
        'We filter through hundreds of applicants to find the top 1% who possess not just the certifications, but the "heart" required for senior care.',

      scopeOfCareTitle: 'The scope of care: what a caregiver does',
      scopeOfCare:
        'Our caregivers typically operate within the **Personal Support Worker (PSW)** scope of practice. They are experts in "Activities of Daily Living" (ADLs).',

      whatTheyCanDoTitle: 'What they can do',
      whatTheyCannotDoTitle: 'What they cannot do',

      scopeOfCareRow1CanDoTitle: 'Personal care',
      scopeOfCareRow1CanDoSubtitle: 'Bathing, dressing, and grooming.',
      scopeOfCareRow1CannotDoTitle: 'Medical procedures',
      scopeOfCareRow1CannotDoSubtitle: 'Injections, sterile wound care, or tube feeding.',

      scopeOfCareRow2CanDoTitle: 'Mobility',
      scopeOfCareRow2CanDoSubtitle: 'Assistance with transfers and walking, getting around their living space.',
      scopeOfCareRow2CannotDoTitle: 'Assessments',
      scopeOfCareRow2CannotDoSubtitle: 'Diagnosing new medical conditions or changing prescriptions.',

      scopeOfCareRow3CanDoTitle: 'Home support',
      scopeOfCareRow3CanDoSubtitle: 'Light housekeeping, laundry, dishes.',
      scopeOfCareRow3CannotDoTitle: 'Heavy labor',
      scopeOfCareRow3CannotDoSubtitle: 'Deep cleaning, snow shovelling, yard work, or moving furniture.',

      scopeOfCareRow4CanDoTitle: 'Nutrition',
      scopeOfCareRow4CanDoSubtitle: 'Meal planning, prep, and feeding assistance.',
      scopeOfCareRow4CannotDoTitle: 'Professional services',
      scopeOfCareRow4CannotDoSubtitle: 'Legal or financial advice.',

      scopeOfCareRow5CanDoTitle: 'Companionship',
      scopeOfCareRow5CanDoSubtitle: 'Meaningful engagement and outings.',
      scopeOfCareRow5CannotDoTitle: '',
      scopeOfCareRow5CannotDoSubtitle: '',

      scopeOfCareRow6CanDoTitle: 'Errands',
      scopeOfCareRow6CanDoSubtitle: 'Run errands with or for your loved one.',
      scopeOfCareRow6CannotDoTitle: '',
      scopeOfCareRow6CannotDoSubtitle: '',

      scopeOfCareRow7CanDoTitle: 'Medication reminders',
      scopeOfCareRow7CanDoSubtitle: 'Timed reminders to take medication (strictly cannot dispense or administer medications).',
      scopeOfCareRow7CannotDoTitle: '',
      scopeOfCareRow7CannotDoSubtitle: '',

      scopeOfCareRow8CanDoTitle: 'Transportation',
      scopeOfCareRow8CanDoSubtitle: 'Accompanying on public or private transportation (strictly cannot drive a vehicle).',
      scopeOfCareRow8CannotDoTitle: '',
      scopeOfCareRow8CannotDoSubtitle: '',

      careQuiltAdvantageTitle: 'The CareQuilt advantage: more than just a match',
      careQuiltAdvantage:
        'While your caregiver focuses on your loved one, our **In-House Care Team** focuses on you. We handle the "nitty-gritty" so you don\'t have to.',

      seamlessLogisticsTitle: 'Seamless logistics',
      seamlessLogistics:
        'We coordinate scheduling, changes, and cancellations.',

      carePlanEvolutionTitle: 'Care plan evolution',
      carePlanEvolution:
        'As your loved one\'s needs change, we update the care plan and keep the caregiver up-to-date.',

      mediationAdvocacyTitle: 'Mediation & advocacy',
      mediationAdvocacy:
        'We act as a professional third party to ensure the relationship between your family and the caregiver remains healthy and transparent.',

      vettingExcellenceTitle: 'Vetting excellence',
      vettingExcellence:
        'Every caregiver undergoes rigorous background checks, including Vulnerable Sector Checks and credential verification.',

      costsAndIncludedTitle: 'Costs and what\'s included',
      costsAndIncluded:
        'Booking ahead helps you keep a consistent caregiver and reduces your hourly rate.',

      weeklyPricingTitle: 'Weekly - $55/hr',
      weeklyPricing:
        'Flexible schedules, caregivers matched based on availability.',

      monthlyPricingTitle: 'Monthly - $45/hr',
      monthlyPricing:
        'Fixed monthly schedules with the same caregiver(s) for the duration of your booking.*',

      quarterlyPricingTitle: 'Quarterly - $35/hr',
      quarterlyPricing:
        'Fixed quarterly schedule with the same caregiver(s) for the duration of your booking.*',

      pricingFootnote: '*Speak with your care manager for more details on pricing.',

      wasHelpful: 'Was this helpful?',
      yes: 'Yes',
      no: 'No',

      question: 'Have a question? We\'ll get back to you in 1 business day or less:',
      submit: 'Submit',

      submissionConfirmationMessage: {
        Title: 'Submitted',
        Subtitle: 'We\'ll get back to you as soon as possible',
      },
    },

    careTeam: {
      title: 'Your care team',
      subtitle: 'They\'re here to support you along the way.',
      jobTitles: {
        careManager: 'Care Manager',
        careConsultant: 'Care Consultant',
      },
      buttons: {
        bookCall: 'Book a Call',
        addCaregiver: 'Add Caregiver',
      },
    },
  },
};
